/**
 * Readers for the eventTimeline's `detail.fields[]` list.
 *
 * THE BACKEND NOW RESOLVES THE FIELDS. Before FIN-12628 the FE fetched the
 * per-type configuration itself (loadFieldConfig.js) and resolved each fieldKey
 * against the payload (resolveField.js); both are deleted. `EventDetail` no
 * longer carries `amount` / `beneficiary` / `destination` / `partner` / `sender`
 * / `device` / `ipAddress` / `customData` at all — it is exactly:
 *
 *   detail: {
 *     eventType: 'LOGIN_MFA_WITH_OTP',
 *     fields: [{ technicalName, displayLabel, value, showSpatial, showOnClick }]
 *   }
 *
 * so the FE's only job is to split that list by the two flags and render
 * `displayLabel: value` (the node draws the value alone and keeps the label for
 * hover and screen readers, FIN-13566). Measured against a live payload on
 * 2026-09-01, not read off the handover doc.
 *
 * An UNCONFIGURED type still gets one default field from the BE — `id`
 * ("Transaction ID") for transactions, `activity_id` ("Activity ID") for
 * activities — so a one-line node means "nobody has configured this type yet",
 * not "the mapping broke".
 */

/** The raw list, tolerating an event the BE did not decorate. */
const list = (event) => {
  const fields = event?.detail?.fields;
  return Array.isArray(fields) ? fields : [];
};

// Does this field carry an actual value? NOT "should it be rendered" — see
// EMPTY_VALUE below for what happens when the answer is no.
const hasValue = (field) => field && field.value != null && field.value !== '';

/**
 * What a CONFIGURED field shows when its value is empty (FIN-13590).
 *
 * REVERSES AN EARLIER DECISION, deliberately. These rows used to be dropped —
 * "a labelled empty row is worse than a shorter panel", agreed on the Confluence
 * thread ("The value can come as blank"). QA raised the cost of that: ticking IP
 * address and Device fingerprint for Password Reset changed the screen not at
 * all, because those columns are NULL for that event, so there was no way to
 * tell "not configured" from "configured but empty" and the settings screen
 * could not be verified from the UI at all.
 *
 * The rule is now: IF AN ADMIN TICKED IT, IT IS ON THE SCREEN. A configured
 * field is a statement that this field matters for this event type, and the
 * honest answer to "what was the IP?" is "we have no value", not silence.
 *
 * ONLY FOR AN EXPLICIT TICK. The identifier, which `identifierRow` shows
 * whatever the stored flags say, keeps the old behaviour and stays hidden when it
 * is empty — nobody chose to display it, so an empty row there would be noise
 * nobody asked for.
 *
 * THE NODE SHOWS IT TOO, not just the panel. The ticket asks whether the
 * space-constrained node should differ from the panel; it does not need to,
 * because the node's configured lines are capped at MAX_SPATIAL_USER_FIELDS, the
 * same cap the settings screen and the backend enforce on save — so an empty row
 * can only ever occupy a slot its own configuration claimed, never push a
 * populated field off the node.
 */
const EMPTY_VALUE = '—';

/** What a collapsed group's configured row shows when its members differ. */
export const MIXED_VALUE = 'Multiple values';

/**
 * DEDUPE ON `technicalName` — working around a live BE defect.
 *
 * `tm.custom_field` holds duplicate rows per `name` (`activity_type` x2,
 * `ip_address` x4) and the join in `fetchConfiguredFields` fans out across them,
 * so every configured field comes back more than once: LOGIN returned **13
 * entries for 6 configured fields** (measured 2026-09-01). Rendering that
 * verbatim doubles every line on the node and in the panel.
 *
 * FIRST OCCURRENCE WINS, which preserves the BE's `sort_order`. Remove this once
 * the duplicate rows are cleaned up upstream — it is defensive, not load-bearing,
 * and a correct payload passes through it unchanged.
 */
const dedupe = (fields) => {
  const seen = new Set();
  return fields.filter((field) => {
    const key = field.technicalName;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * The identifier's technical names, as the TIMELINE sends them.
 *
 * Not `isIdentifier`: the settings endpoint derives that flag, but
 * `detail.fields[]` does not carry it — measured 2026-09-07, the identifier
 * arrives with only `technicalName / displayLabel / value / showSpatial /
 * showOnClick`. So it has to be recognised by key, and the transaction one comes
 * through as plain `id` rather than `transaction_id`.
 */
export const IDENTIFIER_KEYS = new Set(['id', 'activity_id', 'transaction_id']);

/**
 * The event's own timestamp, as the TIMELINE sends it (FIN-13584).
 *
 * THESE ARE WIRE NAMES, NOT CONFIG NAMES, and the two disagree for transactions
 * exactly the way the identifier's do (`transaction_id` in the config arrives as
 * plain `id`). The settings table stores `txn_date_time` / "Transaction
 * Timestamp", but `transactionFieldMap` in StarRocksEventTimelineDao exposes the
 * column as `transaction_date`, so that is the `technicalName` that reaches the
 * FE — confirmed against the payload quoted on FIN-13584 and against the live
 * fixture in __tests__/__live_payload.json.
 *
 * Matching on the config name instead would have left this fix working for
 * activities and silently doing nothing for transactions, which is the case the
 * ticket was raised against.
 *
 * These two are the ONLY time-like keys the BE can emit — grep the DAO's two
 * field maps; there is no completion timestamp on the wire, whatever the
 * settings table offers.
 *
 * PINNED, NOT RENDERED AS ROWS — see PINNED_KEYS below.
 */
export const EVENT_TIME_KEYS = new Set([
  'activity_datetime',
  'transaction_date'
]);

/**
 * `2026-02-15 09:00:00.0` -> `15/02/2026 09:00` (FIN-13584).
 *
 * The ticket's Expected Result writes the configured timestamp as
 * `15/02/2026 14:30`, matching the panel header, while the field arrives as the
 * backend's raw column string. Only the NOTATION is changed here.
 *
 * DELIBERATELY A STRING REWRITE, NOT A DATE PARSE. `detail.fields[]` carries a
 * naive datetime with no zone marker, unlike `event.timestamp`, which is a real
 * ISO-8601 instant. Feeding the naive string to `new Date()` makes the browser
 * guess a zone and silently shifts the value by the local offset — so the panel
 * would show a time the backend never sent. A regex cannot do that.
 *
 * (This is also why the field value and the header can disagree by the server's
 * offset on some environments: the header converts an instant to local time, the
 * field does not. Raised separately — it is a backend serialisation question,
 * and the FE cannot correct a string whose zone is unknown without inventing
 * one.)
 *
 * ANYTHING UNEXPECTED PASSES THROUGH UNTOUCHED. A blank, a pre-formatted value
 * or a shape we have not seen is shown as sent rather than mangled into `—`.
 */
const TIME_TEXT = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/;

export const formatFieldTime = (value) => {
  const text = String(value);
  const parts = TIME_TEXT.exec(text);
  if (!parts) return text;

  const [, year, month, day, hour, minute] = parts;
  return `${day}/${month}/${year} ${hour}:${minute}`;
};

/**
 * The value as the panel and the node line should read it.
 *
 * Applied on the way OUT, never before the group's agreement check: formatting
 * first would drop seconds, so two events a few seconds apart would compare
 * equal and the node would claim they shared a time.
 */
const displayValue = (technicalName, value) =>
  EVENT_TIME_KEYS.has(technicalName) ? formatFieldTime(value) : String(value);

/** `{ label, value }`, the shape both the node lines and the panel rows use. */
export const toRow = (field) => ({
  label: field.displayLabel || field.technicalName,
  value: hasValue(field)
    ? displayValue(field.technicalName, field.value)
    : EMPTY_VALUE
});

/**
 * PINNED FIELDS (FIN-13594) — each has its own fixed slot and never counts as one
 * of the admin's configurable rows:
 *
 *   node:  event type (the label) · date or collapsed range · identifier line,
 *          then up to MAX_SPATIAL_USER_FIELDS configured fields
 *   panel: Event Type · Timestamp · Fraud Score · Event ID (toEvents), then up
 *          to MAX_PANEL_USER_FIELDS configured fields
 *
 * THE TIMESTAMP NEVER RENDERS AS A ROW. The node's date line and the panel's
 * Timestamp row already carry it, so a timestamp row repeated information the
 * node already showed — and spent one of its three slots doing so, pushing an
 * admin's own choice off the node.
 *
 * This is the backend's model too: `validateFields` counts only
 * `fields.filterNot(Set(idKey, dtKey))` against MaxSpatialFields (3) and
 * MaxOnClickFields (15), and the settings screen locks both pinned fields on.
 */
export const PINNED_KEYS = new Set([...IDENTIFIER_KEYS, ...EVENT_TIME_KEYS]);

/** ScalikeEventFieldConfigService.MaxSpatialFields / MaxOnClickFields. */
export const MAX_SPATIAL_USER_FIELDS = 3;
export const MAX_PANEL_USER_FIELDS = 15;

/** The admin's configured fields for one surface, pinned fields excluded. */
const userFields = (event, flag) =>
  dedupe(
    list(event).filter((f) => f && f[flag] && !PINNED_KEYS.has(f.technicalName))
  );

/**
 * The identifier — shown whatever the stored flags say, when it has a value.
 *
 * WHY THE FLAGS ARE IGNORED. The settings screen renders its checkboxes checked
 * and disabled, but that lock is display plus what a SAVE sends; it does not
 * rewrite the database. A brand-new type gets the identifier injected by the BE
 * as `showSpatial = false`, and environments whose rows predate the lock still
 * hold 0/0 or 0/1. Reading the flags would drop the id from the node on exactly
 * those types. An identifier with no value is not rendered as an empty line.
 */
const identifierField = (event) =>
  dedupe(list(event)).find(
    (f) => IDENTIFIER_KEYS.has(f.technicalName) && hasValue(f)
  );

/** The identifier as a `{ label, value }` row, or undefined when there is none. */
export const identifierRow = (event) => {
  const field = identifierField(event);
  return field ? toRow(field) : undefined;
};

/** Configured Spatial Layout fields, capped — the lines under the identifier. */
export const spatialFields = (event) =>
  userFields(event, 'showSpatial').slice(0, MAX_SPATIAL_USER_FIELDS);

/** Configured On Click fields, capped — the typed section of the side panel. */
export const panelFields = (event) =>
  userFields(event, 'showOnClick').slice(0, MAX_PANEL_USER_FIELDS);

/** A node's lines: the identifier, then the admin's Spatial Layout fields. */
export const spatialRows = (event) => {
  const id = identifierRow(event);
  const rows = spatialFields(event).map(toRow);
  return id ? [id, ...rows] : rows;
};

export const panelRows = (event) => panelFields(event).map(toRow);

/**
 * One member's value for a field. THREE OUTCOMES, and they are not the same:
 *   a string  — the member carries the field, with a value
 *   ''        — the member carries it, configured but empty (FIN-13590)
 *   undefined — the member does not carry the field at all
 * Collapsing the last two would make "everybody is empty" and "half of them
 * never had the field" look identical to the agreement check.
 */
const memberValue = (member, technicalName) => {
  const match = list(member).find((f) => f.technicalName === technicalName);
  if (!match) return undefined;
  return hasValue(match) ? String(match.value) : '';
};

/**
 * A COLLAPSED group's configured rows for one surface.
 *
 * A VALUE APPEARS ONLY WHEN EVERY MEMBER AGREES. A burst is one actor on one
 * device, so agreement is the normal case — but printing the first member's
 * value on behalf of all of them would state something false the moment they
 * differ, on a screen whose whole job is evidence. A member missing the field
 * contributes `undefined`, so absence is not agreement.
 *
 * DISAGREEMENT SHOWS MIXED_VALUE, IT DOES NOT DROP THE ROW (FIN-13756). The row
 * used to vanish, so a 309-transaction node whose amounts all differed showed
 * no amount line at all — indistinguishable from "never configured", the same
 * confusion FIN-13590 fixed for empty values on a single event.
 *
 * Driven by the FIRST member's field list so the order matches an expanded
 * child, then capped. Members normally carry the same configured fields — the BE
 * sends a configured field even when its value is empty. The exception is a
 * transaction group spanning two types with one display name (FIN-13893, keyed
 * on label in toEvents.js): a field only the first type configures reads as
 * mixed, since absence is not agreement.
 */
const groupUserRows = (members, flag, max) => {
  if (!members || !members.length) return [];
  return userFields(members[0], flag)
    .map((field) => {
      const values = new Set(
        members.map((member) => memberValue(member, field.technicalName))
      );
      const label = field.displayLabel || field.technicalName;
      if (values.size !== 1) return { label, value: MIXED_VALUE };
      const agreed = [...values][0];
      return {
        label,
        // Every member empty is still agreement — the group genuinely has no
        // value, and the configured row says so rather than vanishing.
        value:
          agreed === ''
            ? EMPTY_VALUE
            : displayValue(field.technicalName, agreed)
      };
    })
    .slice(0, max);
};

/**
 * A collapsed group's identifier: ONE id — the first member that has one — with
 * every member's id in the hover.
 *
 * The identifier can never agree across members, and dropping it made it
 * invisible on every grouped node. Joining the ids was tried and reverted: both
 * surfaces truncate on one line, so "QA-ACT-009-10, QA-ACT-009-…" read as the
 * same id printed twice. The count is already on the node (`ACT ×5`), and the
 * hover still carries every id, so nothing is unreachable.
 */
export const groupIdentifierRow = (members) => {
  if (!members || !members.length) return undefined;
  const ids = members.map(identifierField).filter(Boolean);
  if (!ids.length) return undefined;
  return {
    ...toRow(ids[0]),
    ...(ids.length > 1
      ? { title: ids.map((f) => String(f.value)).join(', ') }
      : {})
  };
};

/** Fact lines under a collapsed group's node: the identifier, then configured fields. */
export const groupSpatialRows = (members) => {
  const id = groupIdentifierRow(members);
  const rows = groupUserRows(members, 'showSpatial', MAX_SPATIAL_USER_FIELDS);
  return id ? [id, ...rows] : rows;
};

/**
 * The typed section of a collapsed group's detail panel — the On Click fields,
 * "Multiple values" where the members disagree. The identifier is pinned in the panel header (toEvents), not here.
 */
export const groupPanelRows = (members) =>
  groupUserRows(members, 'showOnClick', MAX_PANEL_USER_FIELDS);
